export interface Algo {
  id: number;
  name: string;
  description: string;
  parameters: Record<string, any>;
  timeframe: string;
  favorite: boolean;
  created_at: string;
  updated_at: string;
  asset_classes: AssetClass[];
  symbols: Symbol[];
  cash_at_risk: number;
  returns: number;
  instances?: TradingInstance[]; // From trading_instance_algos relationship
}

export interface AssetClass {
  id: number;
  name: string;
  description: string;
  tradable: boolean;
  created_at: string;
  updated_at: string;
  instances?: TradingInstance[]; // From trading_instance_asset_classes relationship
}

export interface Symbol {
  id: number;
  name: string; // This is used as the ticker
  description: string;
  created_at: string;
  updated_at: string;
  instances?: TradingInstance[];
  tradable: boolean;
  max_capital: number;
}

export interface BrokerAccount {
  id: number;
  name: string;
  account_id: string;
  broker_id: number;
  api_key: string;
  api_secret: string;
  created_at: string;
  updated_at: string;
}

export interface Broker {
  id: number;
  name: string;
  accounts: BrokerAccount[];
  created_at: string;
  updated_at: string;
}

export interface TradingInstance {
  id: number;
  name: string;
  broker_account: BrokerAccount;
  broker_account_id: number;
  status: string;
  balance: number;
  initial_balance: number;
  profit: number;
  cash_at_risk?: number;
  linked_algos: LinkedAlgo[];
  trades: Trade[];
  notifications: Notification[];
  asset_classes: AssetClass[];
  symbols: Symbol[];
  created_at: string;
  updated_at: string;
  telegram_enabled: boolean;
  telegram_bot_token?: string;
  telegram_chat_id?: string;
  telegram_notifications?: {
    send_entries: boolean;
    send_updates: boolean;
    send_exits: boolean;
    send_system_notifications: boolean;
    send_hourly_status: boolean;
    include_emoji: boolean;
    include_trade_details: boolean;
  };
}

export enum LotMode {
  FIXED = "fixed",
  RISK_BASED = "risk_based",
}

export enum RiskSource {
  FREE_MARGIN = "free_margin",
  BALANCE = "balance",
  EQUITY = "equity",
}

export enum EntryBar {
  CLOSED = "closed",
  CURRENT = "current",
}

export enum ShutdownDay {
  MONDAY = "Monday",
  TUESDAY = "Tuesday",
  WEDNESDAY = "Wednesday",
  THURSDAY = "Thursday",
  FRIDAY = "Friday",
}

export enum RestartDay {
  MONDAY = "Monday",
  TUESDAY = "Tuesday",
  WEDNESDAY = "Wednesday",
  THURSDAY = "Thursday",
  FRIDAY = "Friday",
}

export enum OrderType {
  MARKET = "market",
  LIMIT = "limit",
}

export enum ExecutionType {
  NORMAL = "normal",
  REVERSE = "reverse",
}

export enum MAType {
  SMA = "SMA",
  EMA = "EMA",
  WMA = "WMA",
}

export enum CrossoverType {
  GOLDEN_CROSS = "GOLDEN_CROSS",
  DEATH_CROSS = "DEATH_CROSS",
}

export interface CrossoverCondition {
  indicator1: string;
  crossType: "Crosses above" | "Crosses below";
  indicator2: string;
}

export interface MovingAverage {
  type: MAType;
  period: number;
  priceType: "close" | "open" | "high" | "low" | "hl2" | "hlc3" | "ohlc4";
  shift: number;
}

export interface RiskParameters {
  // Position Sizing
  lotSizePerSymbol: string;
  lotMode: "Fixed" | "Risk";
  lotValue: number;
  riskSource: "FreeMargin" | "Balance" | "Equity";
  riskPercentage: number;
  maxDrawdownPercentage: number;

  // Stop Loss/Take Profit
  addSpreadToSL: boolean;
  addSpreadToTP: boolean;
  tpPerSymbol: string;
  slPerSymbol: string;

  // Trade Management
  maxOrderCount: number;
  breakevenAfterProfit: number;
  closeTradeAfterDrawdown: number;
  useTrailingStop: boolean;
  trailingStopPoints: number;
  useTrailingTakeProfit: boolean;
  trailingTakeProfitPoints: number;

  // Time Management
  useShutdown: boolean;
  closeDay: ShutdownDay;
  closeTime: string;
  restartDay: RestartDay;
  restartTime: string;
  dailyStartTime: string;
  dailyStopTime: string;

  // Execution Settings
  useIntrabarCalculation: boolean;
  executionReversal: boolean;
  allowPyramiding: boolean;
  maxPyramidPositions: number;
  pyramidSize: number;
  orderType: "Market" | "Limit";

  // Risk Management
  maxRiskPerSymbol: string; // Format: "EURUSD=1000,GBPUSD=500" or "EURUSD=2%,GBPUSD=1.5%"
  maxRiskPerSymbolType: "Fixed" | "Percentage"; // Whether values are $ or %
}

export interface LogicCondition {
  indicator1: MovingAverage;
  relationship: "Crosses above" | "Crosses below" | "Is above" | "Is below";
  indicator2: MovingAverage;
}

export interface MACrossoverParams {
  name: string;
  description: string;
  strategy_type: string;
  movingAverages: MovingAverage[];
  entryConditions: {
    buyConditions: LogicCondition[];
    sellConditions: LogicCondition[];
  };
  exitConditions: {
    buyExitConditions: LogicCondition[];
    sellExitConditions: LogicCondition[];
  };
  riskParameters: RiskParameters;
}

export interface ScheduleParameters {
  activeShutdown: boolean;
  closeDay: string; // Monday, Tuesday, etc.
  closeTime: string; // HH:mm format
  restartDay: string;
  restartTime: string;
  dailyStartTime: string;
  dailyStopTime: string;
}

export interface AlgoParameters {
  // Lot Management
  lotsPerSymbol: string;
  lotMode: LotMode;
  lotValue: number;
  riskSource: RiskSource;
  riskPercent: number;
  maxDD: number;

  // Trade Parameters
  useSLSpread: boolean;
  useTPSpread: boolean;
  tpPerSymbol: string;
  slPerSymbol: string;
  maxOrderCount: number;
  breakevenThreshold: number;
  tradeCloseThreshold: number;
  useTrailingStop: boolean;
  trailingStopPoints: number;
  trailingTakeProfit: boolean;
  trailingTakeProfitPoints: number;

  // Execution Settings
  orderType: OrderType;
  executionType: ExecutionType;
  allowPyramiding: boolean;
  maxPyramidPositions: number;
  pyramidSize: number;
  useIntrabarCalculation: boolean;

  // Time Management
  useShutdown: boolean;
  dailyStartTime: string;
  dailyStopTime: string;
  shutdownDay: ShutdownDay;
  shutdownTime: string;
  restartDay: RestartDay;
  restartTime: string;

  // Schedule Parameters
  scheduleParameters: ScheduleParameters;
}

export interface Notification {
  id: number;
  message: string;
  timestamp: string;
  tags: string[];
  notification_type: string;
  priority: string;
  linked_algo?: {
    id: number;
    name: string;
  };
  trading_instance_id: number;
  created_at: string;
  updated_at: string;
}

export interface Trade {
  id: string;
  trading_instance_id: number;
  symbol: string;
  side: string;
  quantity: number;
  entry_price: number;
  exit_price: number;
  stop_loss_price: number | null;
  take_profit_price: number | null;
  pnl: number;
  net_pnl?: number;
  entry_time: string;
  exit_time: string;
  date: string;
  status: string;
  broker_position_id?: string;
  created_at: string;
  updated_at: string;
}

export interface LinkedAlgo {
  id: number;
  name: string;
  description: string;
  parameters: {
    algorithm: {
      name: string;
      description: string;
      timeframe: string;
      indicators: any[];
      entryConditions: {
        buyConditions: any[];
        sellConditions: any[];
      };
      exitConditions: {
        buyExitConditions: any[];
        sellExitConditions: any[];
      };
    };
    assetClasses: Array<{
      type: string;
      config: {
        sharesPerTrade?: number;
        allowedTradeDirections?: string;
        optionType?: string;
        optionMoneyness?: string;
        daysToExpiration?: number;
        strikeSelectionMethod?: string;
        maxPricePerContract?: number;
        contractsPerTrade?: number;
        lotSize?: number;
        lotValue?: number;
        leverage?: string;
        addSpreadToStopLoss?: boolean;
        addSpreadToTakeProfit?: boolean;
        maxSpendingPerTrade?: number;
        maxTotalSpendingPerDay?: number;
        contractType?: string;
      };
    }>;
    symbols: Array<{
      symbol: string;
      assetClass: string;
      maxCapital: number;
    }>;
    risk: {
      // General Risk Settings
      riskSource: string;
      fixedCapital: number;
      lotMode: string;
      lotValue: number;
      riskPercentage: number;
      maxDollarRisk: number;
      maxOrders: number;
      allowPyramiding: boolean;
      maxPyramidPositions: number;
      pyramidSize: number;

      // Take Profit & Stop Loss Settings
      takeProfitPercentage: number;
      stopLossPercentage: number;
      breakevenAfterProfit: number;
      closeTradeAfterDrawdown: number;

      // Trailing Stop Settings
      trailingStopEnabled: boolean;
      trailingStopType: string;
      trailingStopAmount: number;
      trailingTakeProfitEnabled: boolean;
      trailingTakeProfitType: string;
      trailingTakeProfitAmount: number;

      // Order Settings
      orderType: string;
      limitPriceOffset: number;
      allowReversal: boolean;
    };
    schedule: {
      // Schedule Settings
      activeShutdown: boolean;
      closeDay: string;
      closeTime: string;
      restartDay: string;
      restartTime: string;
      dailyStartTime: string;
      dailyStopTime: string;
      weeklyShutdown: boolean;
      marketOpenTime: string;
      marketCloseTime: string;
    };
  };
  parent_algo_id: number;
  instance_id: number;
  created_at: string;
  asset_classes: AssetClass[];
  symbols: Symbol[];
  cash_at_risk: number;
  returns: number;
  timeframe: string;
}

export interface UTMLPosition {
  id: string;
  algo_id: number;
  symbol: string;
  direction: string;
  entry_price: number | null;
  quantity: number | null;
  entry_time: string | null;
  current_price: number | null;
  unrealized_pnl: number | null;
  realized_pnl: number | null;
  net_pnl: number | null;
  stop_loss_price: number | null;
  take_profit_price: number | null;
  status: string;
  broker_position_id?: string;
}
